import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { StatusBadgeSm } from "../../utils/StatusBadge";
import { ColorPickerButton } from "./ReportFormattingModal";

const ALL_STATUSES = ["COMPLETED", "RUNNING", "FAILED", "PENDING"];

const AUTOGEN_TYPE_LABELS = {
  LIST_OF_FIGURES: "List of Figures",
  LIST_OF_TABLES: "List of Tables",
  ABBREVIATIONS: "Abbreviations",
};

const STATUS_BADGE_CLASS = {
  COMPLETED: "bg-primary",
  RUNNING: "bg-warning text-dark",
  FAILED: "bg-danger",
  PENDING: "bg-secondary",
};

const FilterPickerModal = ({
  isOpen,
  onClose,
  formData,
  agentFilter,
  setAgentFilter,
  selectedAgentIds,
  toggleAgentSelection,
  handleSectionCheckbox,
  isAgentVisible,
  isSectionVisibleUnderFilter,
  getVisibleAgentIdsInSection,
  selectAllVisible,
  deselectAllVisible,
  naturalCompare,
  applyGlobalAgentSetting,
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const addFilterRef = useRef(null);

  // Default: all sections collapsed when modal opens — show only parent sections
  useEffect(() => {
    if (isOpen) {
      const initial = {};
      formData.sectionOrder.forEach((key) => {
        initial[key] = false;
      });
      setExpandedSections(initial);
    }
  }, [isOpen]);

  // ESC to close + body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  // Click-outside for "Add filter" dropdown
  useEffect(() => {
    const handleClick = (e) => {
      if (addFilterRef.current && !addFilterRef.current.contains(e.target)) {
        setAddFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Compute match counts
  const { matchCount, totalAgentCount } = useMemo(() => {
    let match = 0;
    let total = 0;
    Object.values(formData.sectionsDict || {}).forEach((sec) => {
      sec?.agentOrder?.forEach((aKey) => {
        const ag = sec.agentsDict?.[aKey];
        if (!ag) return;
        total += 1;
        if (isAgentVisible(ag)) match += 1;
      });
    });
    return { matchCount: match, totalAgentCount: total };
  }, [formData, agentFilter, isAgentVisible]);

  // Global Extend / Amend / Text Color state — derived from agents that are neither
  // locked nor rich-formatted (same set applyGlobalAgentSetting touches)
  const { globalExtendOn, globalAmendOn, globalAmend1On, globalAmend2On, globalMatchStyleOn, globalColour, unlockedCount } = useMemo(() => {
    const unlocked = [];
    Object.values(formData.sectionsDict || {}).forEach((sec) => {
      sec?.agentOrder?.forEach((aKey) => {
        const ag = sec.agentsDict?.[aKey];
        if (ag && !ag.is_locked && !ag.rich_formatting) unlocked.push(ag);
      });
    });
    const colours = new Set(unlocked.map((ag) => ag.response_colour || null));
    const commonColour = colours.size === 1 ? [...colours][0] : null;
    const eligible = unlocked.filter((ag) => !ag.is_static_text && ag.status === 'COMPLETED');
    return {
      globalExtendOn: eligible.length > 0 && eligible.every((ag) => !!ag.preserve_response),
      globalAmendOn: eligible.length > 0 && eligible.every((ag) => !!ag.amend_response),
      // AMEND 2 (temporary): which amend colouring every eligible agent is on.
      globalAmend1On: eligible.length > 0 && eligible.every((ag) => !!ag.amend_response && ag.amend_version !== 2),
      globalAmend2On: eligible.length > 0 && eligible.every((ag) => !!ag.amend_response && ag.amend_version === 2),
      globalMatchStyleOn: eligible.length > 0 && eligible.every((ag) => !!ag.amend_response && !!ag.match_mapped_style),
      globalColour: commonColour,
      unlockedCount: unlocked.length,
    };
  }, [formData]);

  // Sorted root sections
  const sortedRootSections = useMemo(() => {
    return formData.sectionOrder
      .filter((key) => !formData.sectionsDict[key]?.parent_section_id)
      .sort((a, b) => {
        const oA = formData.sectionsDict[a]?.section_ordering_id || "0";
        const oB = formData.sectionsDict[b]?.section_ordering_id || "0";
        return naturalCompare(oA, oB);
      });
  }, [formData.sectionOrder, formData.sectionsDict]);

  const removeFilter = (status) => {
    setAgentFilter((prev) => {
      const updated = prev.filter((v) => v !== status);
      return updated.length ? updated : ["ALL"];
    });
  };

  const addFilter = (status) => {
    setAgentFilter((prev) => {
      const cleaned = prev.filter((v) => v !== "ALL");
      if (!cleaned.includes(status)) cleaned.push(status);
      return cleaned;
    });
    setAddFilterOpen(false);
  };

  const toggleSectionExpand = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Expand/Collapse All — operates on currently visible sections
  const visibleSectionKeys = useMemo(() => {
    return formData.sectionOrder.filter((key) =>
      isSectionVisibleUnderFilter(key),
    );
  }, [formData.sectionOrder, isSectionVisibleUnderFilter, agentFilter]);

  const areAllExpanded =
    visibleSectionKeys.length > 0 &&
    visibleSectionKeys.every((key) => expandedSections[key]);

  const handleToggleExpandCollapseAll = () => {
    const shouldExpand = !areAllExpanded;
    setExpandedSections((prev) => {
      const next = { ...prev };
      visibleSectionKeys.forEach((key) => {
        next[key] = shouldExpand;
      });
      return next;
    });
  };

  const getChildSections = (parentKey) => {
    return formData.sectionOrder
      .filter(
        (key) => formData.sectionsDict[key]?.parent_section_id === parentKey,
      )
      .sort((a, b) => {
        const oA = formData.sectionsDict[a]?.section_ordering_id || "0";
        const oB = formData.sectionsDict[b]?.section_ordering_id || "0";
        return naturalCompare(oA, oB);
      });
  };

  // Recursive section row renderer
  const renderSectionRow = (sectionKey, depth = 0) => {
    if (!isSectionVisibleUnderFilter(sectionKey)) return null;
    const section = formData.sectionsDict[sectionKey];
    if (!section) return null;

    const visibleIds = getVisibleAgentIdsInSection(sectionKey);
    const isChecked =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedAgentIds.has(id));
    const isIndeterminate =
      visibleIds.length > 0 &&
      !isChecked &&
      visibleIds.some((id) => selectedAgentIds.has(id));
    const isExpanded = !!expandedSections[sectionKey];

    const visibleAgentsHere = (section.agentOrder || []).filter((aKey) => {
      const ag = section.agentsDict[aKey];
      return ag && isAgentVisible(ag);
    });

    const childSections = getChildSections(sectionKey);

    return (
      <div key={sectionKey} className="filter-picker-section-block">
        {/* Section header */}
        <div
          className="filter-picker-section-header d-flex align-items-center ev-card-7 ev-radius-xsmall p-2 mb-1"
          style={{ '--filter-picker-depth': depth }}
        >
          <button
            type="button"
            className="btn p-0 me-2 border-0 bg-transparent ev-small-p-blue"
            onClick={() => toggleSectionExpand(sectionKey)}
            disabled={
              visibleAgentsHere.length === 0 && childSections.length === 0
            }
          >
            <i
              className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"}`}
            ></i>
          </button>

          <input
            type="checkbox"
            className="form-check-input me-2 m-0 cursor-pointer shadow-none"
            checked={isChecked}
            ref={(el) => {
              if (el) el.indeterminate = isIndeterminate;
            }}
            onChange={(e) =>
              handleSectionCheckbox(sectionKey, e.target.checked)
            }
            disabled={visibleIds.length === 0}
            title={
              visibleIds.length === 0
                ? "No selectable agents under this section"
                : "Toggle all visible agents"
            }
          />

          <span className="ev-fs-12 ev-bold-med ev-xsmall-p-blue me-2">
            S.{section.section_ordering_id}
          </span>
          <span className="ev-fs-12 ev-xsmall-p-blue flex-grow-1 text-truncate min-w-0">
            {section.section_name || (
              <em className="ev-xsmall-p-blue">(Unnamed Section)</em>
            )}
          </span>
        </div>

        {/* Agents under this section */}
        {isExpanded &&
          visibleAgentsHere.map((aKey) => {
            const agent = section.agentsDict[aKey];
            const aId = agent.id;
            const isAgentSelected = aId ? selectedAgentIds.has(aId) : false;
            const agentDisplayName =
              agent.agent_name || `Agent ${agent.agent_ordering_id}`;
            const isAutogenSpecial = !!section.autogen_after;
            const autogenLabel = AUTOGEN_TYPE_LABELS[section.autogen_after] || section.autogen_after;

            return (
              <div
                key={aKey}
                className={`ev-card-8 filter-picker-agent-row d-flex align-items-center p-1 mb-1 ev-radius-xsmall ${isAgentSelected ? "filter-picker-agent-row--selected" : ""}`}
                style={{ '--filter-picker-depth': depth }}
              >
                <input
                  type="checkbox"
                  className="form-check-input me-2 m-1 cursor-pointer shadow-none"
                  checked={isAgentSelected}
                  onChange={() => aId && !isAutogenSpecial && toggleAgentSelection(aId)}
                  disabled={!aId || isAutogenSpecial}
                  title={
                    !aId
                      ? "Save preset first to select this agent"
                      : isAutogenSpecial
                        ? `Reserved for the auto-generated ${autogenLabel} — not selectable for run/report`
                        : ""
                  }
                />
                <span className="ev-fs-11 ev-reg-p-dark me-2 text-nowrap">
                  S.{section.section_ordering_id}.{agent.agent_ordering_id}
                </span>
                <span className="ev-fs-12 ev-reg-p-dark flex-grow-1 text-truncate min-w-0">
                  {agentDisplayName}
                </span>
                <span className="ms-2">
                  <StatusBadgeSm status={agent.status || "PENDING"} />
                </span>
              </div>
            );
          })}

        {/* Child sections */}
        {isExpanded &&
          childSections.map((childKey) =>
            renderSectionRow(childKey, depth + 1),
          )}
      </div>
    );
  };

  if (!isOpen) return null;

  const isAllFilter = agentFilter.includes("ALL");
  const activeFilters = isAllFilter ? [] : agentFilter;
  const availableToAdd = ALL_STATUSES.filter((s) => !agentFilter.includes(s));
  const hasAnyVisible = formData.sectionOrder.some((key) =>
    isSectionVisibleUnderFilter(key),
  );

  return createPortal(
    <div
      className="modal fade modal-high-priority show d-block bg-dark bg-opacity-50"
      style={{ zIndex: "var(--num-1000000000)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content ev-message-card2 d-block ev-card-2">
          {/* HEADER */}
          <div className="modal-header border-0 d-flex align-items-center justify-content-between">
            <h5 className="ev-med-p-blue mb-0">
              <i className="bi bi-funnel-fill me-2"></i>
              Modify &amp; Pick Agents
            </h5>
            <button
              type="button"
              className="ev-btn-text-only-fill ev-fs-20"
              onClick={onClose}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>

          {/* FILTER CHIPS BAR — always visible so Add filter is reachable even when no filters active */}
          <div className="ev-card-3 mx-3 ev-padding-xsmall d-flex align-items-center flex-wrap gap-1 ev-radius-small">
            <span className="ev-fs-11 ev-bold-med ev-small-p-blue me-1 p-2">
              Active filters:
            </span>
            {activeFilters.length === 0 && (
              <span className="ev-fs-11 ev-small-p-blue fst-italic">None</span>
            )}
            {activeFilters.map((status) => (
              <span
                key={status}
                className={`badge ev-fs-10 ev-bold-med px-2 py-1 d-flex align-items-center gap-1 rounded-pill ${STATUS_BADGE_CLASS[status] || 'bg-secondary'}`}
              >
                {status}
                <button
                  type="button"
                  className="btn p-0 border-0 bg-transparent ev-fs-11 text-white filter-picker-chip-close"
                  onClick={() => removeFilter(status)}
                  title="Remove filter"
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              </span>
            ))}
            {availableToAdd.length > 0 && (
              <div className="position-relative" ref={addFilterRef}>
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                  onClick={() => setAddFilterOpen((prev) => !prev)}
                >
                  <i className="bi bi-plus-circle me-1"></i> Add filter
                </button>
                {addFilterOpen && (
                  <div
                    className="ev-card-4 position-absolute top-100 mt-2 p-2 z-1000 start-0 filter-picker-add-dropdown"
                  >
                    {availableToAdd.map((status) => (
                      <div
                        key={status}
                        className="ev-card-7 d-flex align-items-center mb-1 p-1 ev-radius-xsmall m-0 cursor-pointer filter-picker-add-option"
                        onClick={() => addFilter(status)}
                      >
                        <span
                          className={`badge ev-fs-10 ev-bold-med px-2 py-1 rounded-pill ${STATUS_BADGE_CLASS[status] || 'bg-secondary'}`}
                        >
                          {status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!isAllFilter && (
              <button
                type="button"
                className="ev-btn-light-fill ev-fs-11 ms-auto"
                onClick={() => setAgentFilter(["ALL"])}
                title="Clear all filters"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* GLOBAL AGENT SETTINGS — Extend · Amend · Text Color for all unlocked agents */}
          {applyGlobalAgentSetting && (
            <div className={`ev-card-3 mx-3 mt-2 ev-padding-xsmall ev-radius-small position-relative ${unlockedCount === 0 ? 'opacity-50' : ''}`} style={{ zIndex: 10 }}>
              {/* Header row sits above the Extend/Amend cards so the colour popover isn't painted under them */}
              <div className="d-flex align-items-center justify-content-between mb-2 px-1 position-relative" style={{ zIndex: 2 }}>
                <span className="ev-fs-11 ev-bold-med ev-small-p-blue">
                  Apply to all agents:
                </span>
                <span className="d-flex align-items-center gap-2 ev-xsmall-p-dark" title="Text color for all unlocked agents">
                  Text color
                  <span
                    className="d-flex align-items-center"
                    style={{ transform: 'scale(0.85)', transformOrigin: 'right center', pointerEvents: unlockedCount === 0 ? 'none' : undefined }}
                    onKeyDownCapture={(e) => { if (unlockedCount === 0) { e.preventDefault(); e.stopPropagation(); } }}
                  >
                    <ColorPickerButton
                      value={globalColour || '#000000'}
                      onChange={(colour) => { if (unlockedCount === 0) return; applyGlobalAgentSetting('response_colour', colour); }}
                      icon={<i className="bi bi-alphabet-uppercase" />}
                    />
                  </span>
                </span>
              </div>

              <div className="d-flex gap-2">
                {[
                  { key: 'preserve_response', on: globalExtendOn, icon: 'bi-layout-text-sidebar', label: 'Extend', hint: 'Keep old response, append new', title: 'Turn Extend on/off for all unlocked agents' },
                  // AMEND 2 (temporary): Amend 1 / Amend 2 replace the single Amend tile.
                  { key: 'amend_response', version: 1, on: globalAmend1On, icon: 'bi-pencil-square', label: 'Amend 1', hint: 'Modify the existing response', title: 'Turn Amend 1 on/off for all unlocked agents' },
                  { key: 'amend_response', version: 2, on: globalAmend2On, icon: 'bi-pencil-square', label: 'Amend 2', hint: 'Modify the existing response', title: 'Turn Amend 2 on/off for all unlocked agents' },
                ].map(({ key, version, on, icon, label, hint, title }) => (
                  <div
                    key={`${key}-${version || 0}`}
                    role="button"
                    aria-pressed={on}
                    title={title}
                    className={`ev-card-2 flex-fill d-flex align-items-start gap-2 p-2 ${on ? 'active-content-style' : ''}`}
                    onClick={() => { if (unlockedCount === 0) return; applyGlobalAgentSetting(key, !on, version); }}
                  >
                    <i className={`bi ${icon} ev-reg-p-blue`} />
                    <div className="d-flex flex-column">
                      <span className="ev-small-p-blue">{label}</span>
                      <span className="ev-xsmall-p-dark opacity-75">{hint}</span>
                    </div>
                    {on && <i className="bi bi-check-circle-fill ev-reg-p-blue ms-auto" />}
                  </div>
                ))}
              </div>

              {/* Match Mapped Style — Amend sub-option, shown only while Amend is on for all agents */}
              {globalAmendOn && (
                <label
                  className="ev-card-2 d-flex align-items-center gap-2 p-2 mt-2 mb-0 cursor-pointer"
                  title="New content follows the same style as the mapped content, for all unlocked agents"
                >
                  <input
                    type="checkbox"
                    className="form-check-input m-0 cursor-pointer shadow-none flex-shrink-0"
                    checked={globalMatchStyleOn}
                    onChange={(e) => { if (unlockedCount === 0) return; applyGlobalAgentSetting('match_mapped_style', e.target.checked); }}
                  />
                  <i className="bi bi-type ev-reg-p-blue" />
                  <div className="d-flex flex-column">
                    <span className="ev-small-p-blue">Match Mapped Style</span>
                    <span className="ev-xsmall-p-dark opacity-75">New content follows the mapped content's writing style</span>
                  </div>
                </label>
              )}
            </div>
          )}

          {/* COUNTER + BULK ACTIONS */}
          <div className="d-flex align-items-center justify-content-between px-3 py-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="ev-message-card4 ev-fs-12 ev-xsmall-p-blue px-2 py-1 rounded-pill">
                <b>{matchCount}</b> of {totalAgentCount} agents match
              </span>
              <span className="ev-message-card5 ev-fs-12 ev-xsmall-p-blue px-2 py-1 rounded-pill">
                {selectedAgentIds.size}{" "}
                selected
              </span>
            </div>
            <div className="d-flex gap-1 flex-wrap">
              <button
                type="button"
                className="ev-btn-light-fill ev-btn-xsmall ev-bold-reg border avatar-tooltip"
                onClick={handleToggleExpandCollapseAll}
                disabled={visibleSectionKeys.length === 0}
                data-tooltip={
                  areAllExpanded
                    ? "Collapse all sections"
                    : "Expand all sections"
                }
              >
                <i
                  className={`bi ${areAllExpanded ? "bi-arrows-collapse" : "bi-arrows-expand"} me-1`}
                ></i>
                {areAllExpanded ? "Collapse All" : "Expand All"}
              </button>
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                onClick={selectAllVisible}
                disabled={matchCount === 0}
              >
                <i className="bi bi-check2-square me-1"></i> Select All Visible
              </button>
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                onClick={deselectAllVisible}
                disabled={selectedAgentIds.size === 0}
              >
                <i className="bi bi-x me-0"></i> Clear Selection
              </button>
            </div>
          </div>

          {/* BODY — sections tree */}
          <div className="modal-body scrollable-element vh-40 pt-0">
            {!hasAnyVisible ? (
              <div className="ev-alert-card-warning my-3">
                <i className="bi bi-info-circle me-1"></i>
                No agents match the current filters. Try removing a filter to
                see more.
              </div>
            ) : (
              sortedRootSections.map((key) => renderSectionRow(key, 0))
            )}
          </div>

          {/* FOOTER */}
          <div className="modal-footer border-0 d-flex align-items-center justify-content-between">
            <span className="ev-fs-11 ev-small-p-blue">
              <i className="bi bi-info-circle me-1"></i>
              Selected agents stay marked across filters — switch views freely
            </span>
            <button
              type="button"
              className="ev-btn-blue-fill-round ev-bold-reg"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FilterPickerModal;